import { useCallback, useRef, useState } from 'react';

import { useTranslateFn } from 'utils/useTranslateFn';
import { showToast } from 'utils/toasts';
import { ToastTypes } from 'common/types/toasts';
import { track } from 'utils/kitAnalytics';
import { reloadWithSession } from 'utils/recoverSessionId';
import { useKitWorkspace } from 'containers/KitWorkspaceStorage/context';
import {
  createWorkspace, saveAssembly, patchAssembly, cloneSession,
} from 'containers/KitWorkspaceStorage/api';
import type { ISaveAssemblyPayload } from 'containers/KitWorkspaceStorage/types';

// Let the success toast paint before "Save and Build Another" reloads the wizard.
const SAVE_TOAST_DELAY_MS = 600;

// Fingerprints of the committed payload, kept in the pending-assembly registry.
// Data covers the configuration itself; content (HTML) is derived from it and
// stays out. Meta covers the user-entered name/notes.
const dataFingerprint = (payload: ISaveAssemblyPayload): string => (
  JSON.stringify({ cart: payload.cartData, summary: payload.summaryData })
);
const metaFingerprint = (payload: ISaveAssemblyPayload): string => (
  JSON.stringify([payload.name ?? '', payload.notes ?? ''])
);

interface IUseKitConfirmActionsArgs {
  sessionId?: string;
  // Builds the standalone-assembly payload from the current wizard state. Each
  // tenant supplies its own (different domain model + HTML template); the
  // confirm page injects the user-entered name/notes into it.
  buildPayload: () => ISaveAssemblyPayload;
  // FormSwitcher reset (hash='new'; reload) for "Save and Build Another".
  onResetHandler: () => void;
}

/**
 * Shared confirm/last-page kit logic so per-project SummaryViews stay thin.
 * Handles Save and Build Another / Save and Copy and committing the pending
 * assembly into the workspace. PDF generation is NOT here: both the confirm page
 * and Manage Kits open the shared Combined PDF preview popup, which generates the
 * whole-workspace PDF. Tenant differences (domain model, HTML, cart event) stay
 * in the SummaryView via buildPayload.
 *
 * The current confirm-page assembly has one stable identity per client session,
 * kept in the KitWorkspaceStorage pending-assembly registry (it survives the
 * confirm page unmounting, e.g. a round trip via the step tabs). Once committed
 * (via "My assemblies", "Save and Build Another", "Save and Copy" or the PDF
 * preview), every later commit for the same session UPDATES that row in place:
 * unchanged payload is a no-op, changed name/notes patch the meta, a changed
 * configuration patches the full payload. Edits made after leaving the confirm
 * page land on a fresh cloned session (see SessionRotationBridge) and become a
 * new assembly, which keeps analytics at one session per assembly.
 */
export const useKitConfirmActions = ({
  sessionId, buildPayload, onResetHandler,
}: IUseKitConfirmActionsArgs) => {
  const t = useTranslateFn<string>();
  const {
    workspaceKey, setWorkspaceKey, refetch, getPendingAssembly, rememberPendingAssembly,
  } = useKitWorkspace();

  const [isSaving, setIsSaving] = useState(false);
  // Dedupes concurrent persist calls (StrictMode double-mount, My assemblies + the
  // preview popup firing together) so the pending assembly is saved exactly once.
  const persistInFlightRef = useRef<Promise<string | null> | null>(null);
  // Dedupe workspace creation. The first commit calls ensureWorkspace twice in one
  // flow (commitPendingAssembly, then runPersist), and workspaceKey state has not
  // propagated yet, so a naive check would create TWO workspaces: the assembly
  // lands in one while the caller returns the other, and the preview then reads an
  // empty workspace (the "0 items" flash that a second Preview click fixed). The
  // ref survives across renders so both calls resolve to the same new key.
  const createdKeyRef = useRef<string | null>(null);
  const ensureInFlightRef = useRef<Promise<string | null> | null>(null);

  // Ensure a workspace exists, return its key.
  const ensureWorkspace = useCallback((): Promise<string | null> => {
    if (workspaceKey) return Promise.resolve(workspaceKey);
    if (createdKeyRef.current) return Promise.resolve(createdKeyRef.current);
    if (!ensureInFlightRef.current) {
      ensureInFlightRef.current = (async (): Promise<string | null> => {
        if (!sessionId) {
          showToast(ToastTypes.error, t('texts.kits.toast.missingSessionId'));
          return null;
        }
        const { workspaceKey: key } = await createWorkspace(sessionId);
        createdKeyRef.current = key;
        setWorkspaceKey(key);
        return key;
      })().finally(() => { ensureInFlightRef.current = null; });
    }
    return ensureInFlightRef.current;
  }, [workspaceKey, sessionId, setWorkspaceKey, t]);

  // Create the current pending assembly the first time, then UPDATE it in place
  // on every later call instead of adding a duplicate. Skips the request when
  // nothing changed since the last commit. Returns the assembly id.
  const persistOrUpdateAssembly = useCallback(async (): Promise<string | null> => {
    const key = await ensureWorkspace();
    if (!key || !sessionId) return null;
    const payload = buildPayload();
    const dataFp = dataFingerprint(payload);
    const metaFp = metaFingerprint(payload);

    const existing = getPendingAssembly(sessionId);
    if (existing) {
      if (existing.dataFp === dataFp && existing.metaFp === metaFp) {
        return existing.assemblyId;
      }
      try {
        if (existing.dataFp === dataFp) {
          await patchAssembly(existing.assemblyId, { name: payload.name ?? '', notes: payload.notes ?? '' });
        } else {
          await patchAssembly(existing.assemblyId, { ...payload, name: payload.name ?? '', notes: payload.notes ?? '' });
        }
        rememberPendingAssembly(sessionId, { assemblyId: existing.assemblyId, dataFp, metaFp });
        track('assembly_updated', { workspaceKey: key, assemblyId: existing.assemblyId, hasName: !!payload.name });
        await refetch();
        return existing.assemblyId;
      } catch (e) {
        if ((e as { status?: number })?.status !== 404) throw e;
        // The assembly is gone (deleted, or the workspace was reset/expired):
        // fall through and save it as a new one.
      }
    }

    const { assemblyId } = await saveAssembly(key, payload);
    rememberPendingAssembly(sessionId, { assemblyId, dataFp, metaFp });
    track('assembly_saved', { workspaceKey: key, assemblyId, hasName: !!payload.name });
    await refetch();
    return assemblyId;
  }, [ensureWorkspace, sessionId, buildPayload, getPendingAssembly, rememberPendingAssembly, refetch]);

  // Single deduped entry point: concurrent callers share one in-flight promise so
  // the create path never runs twice.
  const runPersist = useCallback((): Promise<string | null> => {
    if (!persistInFlightRef.current) {
      persistInFlightRef.current = persistOrUpdateAssembly()
        .finally(() => { persistInFlightRef.current = null; });
    }
    return persistInFlightRef.current;
  }, [persistOrUpdateAssembly]);

  // Ensure the current confirm-page assembly is in the workspace. Used by the
  // "My assemblies" button and the Combined PDF preview so the pending assembly
  // lands in the order before it is shown/generated. Patches name/notes if they
  // changed since the last commit. Returns the key.
  const commitPendingAssembly = useCallback(async (): Promise<string | null> => {
    const key = await ensureWorkspace();
    if (!key) return null;
    try { await runPersist(); } catch { /* leave the existing items as-is */ }
    return key;
  }, [ensureWorkspace, runPersist]);

  // "Save and Copy": save/update the current assembly, then reopen the builder
  // from the first step pre-filled with the same config (a cloned session), so
  // the user can change one thing (e.g. a fitting or accessory) and save it as a
  // new assembly. It does NOT auto-create a copy — the variant is whatever the
  // user saves next. The clone is its own client session, so the variant counts
  // as a separate configuration in analytics.
  const handleSaveAndCopy = useCallback(async () => {
    if (!sessionId) return;
    setIsSaving(true);
    try {
      await runPersist();
      const { sessionId: clonedSessionId } = await cloneSession(sessionId);
      track('session_reset', { workspaceKey: workspaceKey ?? undefined, reason: 'save_and_copy' });
      showToast(ToastTypes.success, t('texts.kits.toast.saved'));
      // Let the success toast paint before the wizard reloads.
      await new Promise((resolve) => { setTimeout(resolve, SAVE_TOAST_DELAY_MS); });
      reloadWithSession(clonedSessionId);
    } catch {
      showToast(ToastTypes.error, t('texts.kits.toast.saveError'));
      setIsSaving(false);
    }
  }, [sessionId, runPersist, workspaceKey, t]);

  const handleSaveAndBuildAnother = useCallback(async () => {
    setIsSaving(true);
    try {
      await runPersist();
      track('session_reset', { workspaceKey: workspaceKey ?? undefined, reason: 'save_and_build_another' });
      showToast(ToastTypes.success, t('texts.kits.toast.saved'));
      // Let the success toast paint before the wizard reloads.
      await new Promise((resolve) => { setTimeout(resolve, SAVE_TOAST_DELAY_MS); });
      onResetHandler();
    } catch {
      showToast(ToastTypes.error, t('texts.kits.toast.saveError'));
      setIsSaving(false);
    }
  }, [runPersist, onResetHandler, workspaceKey, t]);

  return {
    isSaving,
    commitPendingAssembly,
    handleSaveAndCopy,
    handleSaveAndBuildAnother,
  };
};

export default useKitConfirmActions;
